<template>
  <div class="columns-block" :class="[widthClass, gapClass]">
    <!-- Wrap mode: float-based layout -->
    <div v-if="isWrapMode" class="columns-wrap" :class="[`wrap-media-${mediaPosition}`]">
      <div class="wrap-media" :style="{ width: mediaWidthPercent + '%' }">
        <component
          :is="slotComponents[mediaSlot.type]"
          v-if="mediaSlot && slotComponents[mediaSlot.type]"
          :data="mediaSlot.data"
          v-bind="slotExtraProps(mediaSlot)"
        />
      </div>
      <component
        :is="slotComponents[textSlot.type]"
        v-if="textSlot && slotComponents[textSlot.type]"
        :data="textSlot.data"
      />
    </div>

    <!-- Normal mode: flex-based layout -->
    <div v-else class="columns-row">
      <div
        v-for="(slot, index) in visibleSlots"
        :key="index"
        class="column-slot"
        :style="{ flex: columnFlex[index] }"
      >
        <component
          :is="slotComponents[slot.type]"
          v-if="slot && slot.type && slotComponents[slot.type]"
          :data="slot.data"
          v-bind="slotExtraProps(slot)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TextBlock from './TextBlock.vue'
import ImageBlock from './ImageBlock.vue'
import ButtonBlock from './ButtonBlock.vue'
import VideoBlock from './VideoBlock.vue'
import HeadingBlock from './HeadingBlock.vue'

const WRAP_TYPES = ['image', 'video', 'button']
const TEXT_TYPES = ['text', 'heading']

export default {
  name: 'ColumnsBlock',
  components: { TextBlock, ImageBlock, ButtonBlock, VideoBlock, HeadingBlock },
  props: {
    data: {
      type: Object,
      required: true
    },
    // Forwarded to image slots so an above-the-fold columns image loads eagerly.
    eager: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    columnCount() {
      return this.data.columnCount || 2
    },
    visibleSlots() {
      const slots = this.data.slots || []
      // Pad with nulls if fewer slots than columnCount
      const result = []
      for (let i = 0; i < this.columnCount; i++) {
        result.push(slots[i] || null)
      }
      return result
    },
    columnFlex() {
      const ratio = this.data.ratio
      if (Array.isArray(ratio) && ratio.length) return ratio
      return this.columnCount === 3 ? [1, 1, 1] : [1, 1]
    },
    widthClass() {
      return this.data.width === 'content' ? 'columns-width-content' : 'columns-width-full'
    },
    gapClass() {
      const gap = this.data.gap || 'medium'
      return `columns-gap-${gap}`
    },
    slotComponents() {
      return {
        text: TextBlock,
        image: ImageBlock,
        button: ButtonBlock,
        video: VideoBlock,
        heading: HeadingBlock
      }
    },
    isWrapMode() {
      if (!this.data.wrapText || this.columnCount !== 2) return false
      const slots = this.data.slots || []
      if (slots.length < 2 || !slots[0] || !slots[1]) return false
      const hasMedia = WRAP_TYPES.includes(slots[0].type) || WRAP_TYPES.includes(slots[1].type)
      const hasText = TEXT_TYPES.includes(slots[0].type) || TEXT_TYPES.includes(slots[1].type)
      return hasMedia && hasText
    },
    mediaSlot() {
      if (!this.isWrapMode) return null
      const slots = this.data.slots
      return WRAP_TYPES.includes(slots[0].type) ? slots[0] : slots[1]
    },
    textSlot() {
      if (!this.isWrapMode) return null
      const slots = this.data.slots
      return TEXT_TYPES.includes(slots[0].type) ? slots[0] : slots[1]
    },
    mediaPosition() {
      if (!this.isWrapMode) return 'left'
      return WRAP_TYPES.includes(this.data.slots[0].type) ? 'left' : 'right'
    },
    mediaWidthPercent() {
      const ratio = this.data.ratio
      if (!Array.isArray(ratio) || ratio.length < 2) return 50
      return this.mediaPosition === 'left' ? ratio[0] : ratio[1]
    }
  },
  methods: {
    // Only ImageBlock consumes `eager`; don't leak the attribute onto other slot types.
    slotExtraProps(slot) {
      return slot?.type === 'image' ? { eager: this.eager } : {}
    }
  }
}
</script>

<style scoped>
.columns-block {
  width: 100%;
  margin: 0 auto;
}

.columns-width-full {
  max-width: 1200px;
}

.columns-width-content {
  max-width: 800px;
}

.columns-row {
  display: flex;
  align-items: flex-start;
}

.columns-gap-small .columns-row {
  gap: 12px;
}

.columns-gap-medium .columns-row {
  gap: 24px;
}

.columns-gap-large .columns-row {
  gap: 40px;
}

.column-slot {
  min-width: 0;
  text-align: left;
}

/* Wrap mode: text flows around floated media */
.columns-wrap {
  overflow: hidden; /* contain floats */
  text-align: left;
}

.wrap-media {
  max-width: 70%;
  min-width: 100px;
}

.wrap-media-left .wrap-media {
  float: left;
}

.wrap-media-right .wrap-media {
  float: right;
}

.columns-gap-small .wrap-media-left .wrap-media {
  margin: 0 12px 8px 0;
}
.columns-gap-small .wrap-media-right .wrap-media {
  margin: 0 0 8px 12px;
}

.columns-gap-medium .wrap-media-left .wrap-media {
  margin: 0 24px 12px 0;
}
.columns-gap-medium .wrap-media-right .wrap-media {
  margin: 0 0 12px 24px;
}

.columns-gap-large .wrap-media-left .wrap-media {
  margin: 0 40px 16px 0;
}
.columns-gap-large .wrap-media-right .wrap-media {
  margin: 0 0 16px 40px;
}

/* Admin previews always show the desktop page */
@media only screen and (max-width: 768px) {
  :where(.columns-block:not(.preview-page *)) {
    .columns-row {
      flex-direction: column;
    }

    .column-slot {
      flex: 1 1 100% !important;
    }

    .wrap-media {
      float: none !important;
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 0 16px 0 !important;
    }
  }
}
</style>
